import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssetsOptions, useAssignmentsActions } from '../../hooks';
import {
  CreateAssignmentsSchema,
  createAssignmentsSchema,
  defaultValues,
} from '../../models';
import {
  FormDateInput,
  FormSubmitButton,
  GlobalButton,
  NavigateLinks,
} from '@/components';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { AssetSelector, SearchUser, AssetDisplay, AssetSelectorData } from '@/pages/Inventory/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { assignmentsPageBreadcrumbs } from '../../config';
import { useUsersOptions } from '@/pages/Inventory/hooks';
import { useGetAssignmentQuery } from '../../service';

const EditAssignments = () => {
  const { id = '' } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { onUpdateAssignment, isUpdatingAssignment, onDeleteAssignment } =
    useAssignmentsActions();

  const { data: assignmentToEdit, isLoading: isLoadingAssignment } =
    useGetAssignmentQuery({ assignmentId: id }, { skip: !id });

  const { control, handleSubmit, reset, setValue, formState, watch } =
    useForm<CreateAssignmentsSchema>({
      resolver: zodResolver(createAssignmentsSchema),
      defaultValues,
      mode: 'onChange',
      shouldFocusError: false,
    });

  const [selectedAsset, setSelectedAsset] = useState<AssetSelectorData | null>(null);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const { assetsOptions } = useAssetsOptions({ returnRaw: false });
  const { userOptions } = useUsersOptions({});
  const breadcrumbs = useMemo(() => assignmentsPageBreadcrumbs(navigate), []);

  useEffect(() => {
    if (assignmentToEdit && assetsOptions.length > 0) {
      const assetOption = assetsOptions.find(
        (option) => option.data.id === assignmentToEdit.assetId
      );
      setSelectedAsset(assetOption ? assetOption.data : null);
      reset({
        assetId: assignmentToEdit.assetId,
        userId:
          typeof assignmentToEdit.userId === 'string'
            ? assignmentToEdit.userId
            : '',
        returnedAt: assignmentToEdit.returnedAt
          ? new Date(assignmentToEdit.returnedAt)
          : undefined,
      });
    }
  }, [assignmentToEdit, assetsOptions, reset]);

  const handleAssetSelected = (asset: AssetSelectorData) => {
    setSelectedAsset(asset);
    setValue('assetId', asset.id, { shouldValidate: true });
  };

  const userIdValue = watch('userId');
  const selectedUserOption =
    userOptions.find((opt) => opt.data.id === userIdValue) || null;

  if (isLoadingAssignment) {
    return (
      <section className='flex flex-col w-full h-full gap-10'>
        <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg'>Cargando asignación...</div>
        </div>
      </section>
    );
  }

  if (!assignmentToEdit) {
    return (
      <section className='flex flex-col w-full h-full gap-10'>
        <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>Asignación no encontrada</div>
        </div>
      </section>
    );
  }

  return (
    <section className='flex flex-col w-full h-full gap-5'>
      <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
        <div className='w-full max-w-2xl mx-auto flex justify-end'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => {
              if (
                window.confirm(
                  '¿Estás seguro de que deseas eliminar esta asignación?'
                )
              ) {
                onDeleteAssignment(id, () =>
                  navigate('/inventory/assignments')
                );
              }
            }}
          >
            <DeleteIcon className='text-red-600' />
            <span>Eliminar asignación</span>
          </GlobalButton>
        </div>
        <form className='flex flex-col gap-6 bg-white p-12 rounded-lg shadow-md mx-auto max-w-2xl w-full'>
          <h1 className='text-3xl font-black select-none'>Editar asignación</h1>
          <AssetDisplay
            selectedAsset={selectedAsset}
            onClear={() => {
              setSelectedAsset(null);
              setValue('assetId', '', { shouldValidate: true });
            }}
            onSearch={() => setAssetSelectorOpen(true)}
            showDetails={false}
            showWarning={false}
          />
          <AssetSelector
            open={assetSelectorOpen}
            onClose={() => setAssetSelectorOpen(false)}
            onSelect={handleAssetSelected}
          />
          <SearchUser
            name='userId'
            placeholder='Usuario'
            control={control}
            required
            onUserSelect={(userId) => {
              setValue('userId', userId, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            showSelectedUserInfo={true}
            value={selectedUserOption}
          />
          <div className='relative flex items-center'>
            <FormDateInput
              name='returnedAt'
              placeholder='Fecha de devolución'
              control={control}
            />
          </div>
          <div className='flex justify-end gap-4 mt-2'>
            <FormSubmitButton
              icon={<SaveIcon />}
              action={handleSubmit((formData) => {
                const payload = {
                  ...formData,
                  returnedAt:
                    formData.returnedAt == null ? null : formData.returnedAt,
                };
                onUpdateAssignment(id, payload, () =>
                  navigate('/inventory/assignments')
                );
              })}
              disabled={!formState.isDirty || !formState.isValid}
              isLoading={isUpdatingAssignment}
              label='Actualizar asignación'
            />
          </div>
        </form>
    </section>
  );
};

export default EditAssignments;
